import { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export type AdminStatus = 'checking' | 'authorized' | 'denied';

// Module-level cache so multiple components (AdminRoute, AppLayout nav,
// etc.) don't each fire their own probe. Cleared by signOut in AuthContext.
let cache: 'authorized' | 'denied' | null = null;
let inflight: Promise<'authorized' | 'denied'> | null = null;

function resolveInitial(): AdminStatus {
  if (cache === 'authorized') return 'authorized';
  if (cache === 'denied') return 'denied';
  return 'checking';
}

function probe(): Promise<'authorized' | 'denied'> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = api
    .get('/admin/writers', { params: { per_page: 1 } })
    .then(() => {
      cache = 'authorized';
      return cache;
    })
    .catch(() => {
      cache = 'denied';
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useAdminStatus(): AdminStatus {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState<AdminStatus>(resolveInitial());

  useEffect(() => {
    if (!user || loading) return;
    if (cache) {
      setStatus(cache);
      return;
    }
    let active = true;
    probe().then((result) => {
      if (active) setStatus(result);
    });
    return () => {
      active = false;
    };
  }, [user, loading]);

  return status;
}

export function clearAdminStatusCache() {
  cache = null;
  inflight = null;
}
